import { FC, ReactNode, useEffect, useRef } from 'react'
import { Spinner } from 'react-bootstrap';
import { useAuth, useFirestore, useSigninCheck } from 'reactfire';
import { LoginPage } from '../pages/LoginPage';
import useQueryParam from '../hooks/useQueryParams';
import { COLLECTIONS } from '../utils/shared/constants';
import { signOut, signInWithCustomToken } from 'firebase/auth';
import { Header } from './Header';
import { toast } from 'react-toastify';
import { UserContextProvider } from '../context/UserContext';
import { OrganizationContextProvider } from '../context/OrganizationContext';
import SaveToastContextProvider from '../context/SaveToastContext';
import { useNavigate } from 'react-router-dom';
import { TextProvider } from '../context/TextContext';
import { UserInvitation } from '../models/shared/UserInvitation';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { fromFirebaseDoc } from '../utils/shared/firebase';
import { getBrowserLanguage } from '../utils/presentation';
import Footer from './Footer';
import { loginVerify } from '../utils/api';

interface IProps {
    children: ReactNode
    fullScreen?: boolean
    noSearch?: boolean
    isInvite?: boolean
}

/**
* @author
* @function @PrivateRoute
**/

export const PrivateRoute: FC<IProps> = (props) => {
    const { status, data: signInCheckResult } = useSigninCheck();
    const { authToken, orgId, id } = useQueryParam();
    const auth = useAuth();
    const navigate = useNavigate();
    const firestore = useFirestore();
    const verifyingTokenRef = useRef<string | null>(null);

    useEffect(() => {
        if (!authToken || signInCheckResult?.signedIn) {
            return;
        }

        // Prevent multiple verification attempts for the same token
        if (verifyingTokenRef.current === authToken) {
            return;
        }

        verifyingTokenRef.current = authToken;

        const verifyAndSignIn = async () => {
            try {

                const result = await loginVerify(authToken, getBrowserLanguage());
                await signInWithCustomToken(auth, result.customToken);

                // Remove authToken from URL after successful authentication
                const url = new URL(window.location.href);
                url.searchParams.delete('authToken');
                window.history.replaceState({}, '', url.toString());
            } catch (e) {
                console.error('Failed to verify auth token', e);
                toast.error('Failed to authenticate');
            }
        };

        verifyAndSignIn();
    }, [authToken, auth, signInCheckResult?.signedIn])

    useEffect(() => {
        const currentUserEmail = auth.currentUser?.email;
        if (!props.isInvite || !currentUserEmail) {
            return;
        }

        const markInviteAccepted = async () => {
            const inviteRef = doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.USER_INVITATIONS}/${id}`);
            const userInviteSnap = await getDoc(inviteRef);
            const userInvite = fromFirebaseDoc<UserInvitation>(userInviteSnap);
            console.log('User invite', userInvite);
            if (userInvite.status !== 'accepted' && userInvite.email.toLowerCase() === currentUserEmail.toLowerCase()) {
                const update: Partial<UserInvitation> = {
                    acceptedAt: new Date(),
                    status: 'accepted'
                }
                await setDoc(inviteRef, update, { merge: true });
            }

            navigate('/');

        }
        markInviteAccepted();
    }, [props.isInvite, auth.currentUser, firestore, orgId, id, navigate]);



    if (status === 'loading') {
        return <div className='h-100 center'><Spinner /></div>
    }

    if (signInCheckResult?.signedIn === false || props.isInvite) {
        if (authToken) {
            return <TextProvider language={getBrowserLanguage()}>
                <div className='h-100 center'><Spinner /></div>
            </TextProvider>
        }
        return <TextProvider language={getBrowserLanguage()}>
            <LoginPage isInvite={props.isInvite} />
        </TextProvider>
    }

    const handleLogout = () => {
        signOut(auth);
        navigate('/');
    }

    const containerClassname = props.fullScreen ? 'flex-1' : 'flex-1 container pt-2 pb-3';
    return (
        <UserContextProvider>
            <SaveToastContextProvider>
                <OrganizationContextProvider>
                    <div className='d-flex flex-column h-100'>
                        <Header handleLogout={handleLogout} noSearch={props.noSearch} fullScreen={props.fullScreen} />
                        <div className={containerClassname}>
                            {props.children}
                        </div>
                        <Footer />
                    </div>
                </OrganizationContextProvider>
            </SaveToastContextProvider>
        </UserContextProvider>
    )
}
